export type SingleFlight<T> = {
  isRunning: () => boolean;
  run: (operation: () => Promise<T>) => Promise<T>;
};

export function createSingleFlight<T>(): SingleFlight<T> {
  let currentOperation: Promise<T> | null = null;

  return {
    isRunning: () => currentOperation !== null,
    run(operation) {
      if (currentOperation) {
        return currentOperation;
      }

      currentOperation = operation().finally(() => {
        currentOperation = null;
      });

      return currentOperation;
    },
  };
}

export type KeyedSingleFlight<TKey, TValue> = {
  isRunning: (key: TKey) => boolean;
  run: (key: TKey, operation: () => Promise<TValue>) => Promise<TValue>;
};

export function createKeyedSingleFlight<TKey, TValue>(): KeyedSingleFlight<
  TKey,
  TValue
> {
  const currentOperations = new Map<TKey, Promise<TValue>>();

  return {
    isRunning: (key) => currentOperations.has(key),
    run(key, operation) {
      const currentOperation = currentOperations.get(key);
      if (currentOperation) {
        return currentOperation;
      }

      const nextOperation = operation().finally(() => {
        currentOperations.delete(key);
      });
      currentOperations.set(key, nextOperation);

      return nextOperation;
    },
  };
}
